import type {
	ColumnConfiguration,
	FieldConfiguration,
	ListBuilderConfiguration,
	MobileCardConfiguration,
} from '../types';

/**
 * Zones mobile card for item lists (PD-14059). Items has no client/address
 * block, so there is no wide column for short facts to pair with — Item # /
 * NDC / Category / On Hand flow as inline chips (the engine's single-column
 * form), which is already the height-minimal shape per the design review.
 * The title (name + manufacturer + attribute icons) wraps instead of
 * front-truncating, the empty image-placeholder block is hidden, and Target
 * price is the hero. Sales/analytics columns are desktop-only noise on a
 * phone card. On Order / Soonest Arr are hidden by pair ref so the trailing
 * Restocked/Added date pair still shows on the tabs whose whole point it is.
 */
const ITEM_MOBILE_CARD_CONFIG: MobileCardConfiguration = {
	cardLayout: 'zones',
	primaryFields: ['item-info'],
	heroField: 'pricing.target-price-field',
	hiddenFields: [
		'image',
		'pricing.minimum-price-field',
		'quantity.soonest-expiration-field',
		'quantity.quantity-on-backorder-field',
		'on-order.quantity-on-order-field',
		'on-order.soonest-arrival-field',
		'item-cost',
		'sales-aggregate',
		'client-reach',
		'rolling-revenue',
		'sales-trend-chart',
	],
};

/**
 * Item list configuration.
 *
 * The four default item tabs (All Items, Active Items, Recently Restocked,
 * Newly Stocked) ship the rich column layout that Axia curated in production
 * via the list-builder editor — promoted here so it becomes the baseline for
 * every tenant. The layout: image, Item/Manufacturer, Item#/NDC/Category,
 * Target/Min pricing, On Hand/Soonest Exp/Backorder, On Order/Soonest Arr,
 * Item Cost, 3-month sales aggregates, rolling revenue trend, and a 1-year
 * sales sparkline.
 *
 * Recently Restocked and Newly Stocked reuse that exact layout and only add a
 * date stacked BELOW "Soonest Arr" inside the On Order column (restocked date /
 * added date respectively), plus a last-30-days filter and a date-descending
 * default sort.
 */

/**
 * Empty line-break field used to stack multiple values inside one column cell.
 * The gray variant matches the subtle separator color Axia used between stacked
 * sub-values; the default variant is used inside the Item/Manufacturer cell.
 */
const getLineBreak = (id: string, gray = true): FieldConfiguration => ({
	id,
	dataConfig: { type: 'custom_text' as const, customText: '' },
	styleConfig: {
		size: 'md' as const,
		colorRule: gray
			? { type: 'theme' as const, themeColor: 'textMuted' }
			: { type: 'default' as const },
	},
	displayStyle: 'line_break' as const,
});

/**
 * Shared column configuration for item image
 */
const getImageColumn = (): ColumnConfiguration => ({
	id: 'image',
	width: 60,
	fields: [
		{
			id: 'item_image',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'primaryImageUrl', objectTable: 'item' },
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
				imageOptions: {
					size: 'small' as const,
					enableViewer: true,
				},
			},
			displayStyle: 'image' as const,
		},
	],
	headers: [{ id: 'image_header', text: '' }],
	sortable: false,
	alignment: 'center' as const,
	verticalAlignment: 'middle' as const,
});

/**
 * Item / Manufacturer cell. Description links to the item detail; the
 * shop-item-attribute-icons custom component renders the hazmat / refrigerated
 * / short-date badges. The component derives the icons from row data, so the
 * field it is bound to is irrelevant — we bind it to the always-present `id`.
 */
const getItemInfoColumn = (): ColumnConfiguration => ({
	id: 'item-info',
	label: 'Item',
	width: 250,
	fields: [
		{
			id: 'name-field',
			link: {
				type: 'object' as const,
				enabled: true,
				objectTable: 'item',
			},
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'description', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'text' as const,
		},
		getLineBreak('lb1', false),
		{
			id: 'manufacturer-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'manufacturer', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
			},
			displayStyle: 'text' as const,
		},
		getLineBreak('lb2', false),
		{
			id: 'item_attribute_icons',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'id', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
			},
			displayStyle: 'custom' as const,
			customComponent: { id: 'shop-item-attribute-icons' },
		},
	],
	headers: [
		{ id: 'h1', text: 'Name' },
		{ id: 'item-info-manufacturer-header', text: 'Manufacturer' },
	],
	minWidth: 200,
	sortable: true,
	alignment: 'left' as const,
	sortFields: [
		{
			direction: 'ASC' as const,
			fieldName: 'description',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top' as const,
});

/**
 * Item identifiers cell: Item # / NDC / Category.
 *
 * "Item #" falls back through externalId2 → externalId → id so a value always
 * renders regardless of which identifier a given tenant populates. (Axia's two
 * saved tabs had drifted to different single fields here; this normalizes them.)
 */
const getItemIdentifiersColumn = (): ColumnConfiguration => ({
	id: 'item-identifiers',
	width: 125,
	fields: [
		{
			id: 'item-number-field',
			dataConfig: {
				type: 'conditional' as const,
				conditions: [
					{
						field: 'externalId2',
						operator: 'not_equals' as const,
						value: null,
						dataConfig: {
							type: 'field' as const,
							field: { fieldName: 'externalId2', objectTable: 'item' },
						},
					},
					{
						field: 'externalId',
						operator: 'not_equals' as const,
						value: null,
						dataConfig: {
							type: 'field' as const,
							field: { fieldName: 'externalId', objectTable: 'item' },
						},
					},
				],
				defaultDataConfig: {
					type: 'field' as const,
					field: { fieldName: 'id', objectTable: 'item' },
					defaultValue: '-',
				},
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'text' as const,
		},
		getLineBreak('item-identifiers-lb1'),
		{
			id: 'ndc-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'ndc', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
			},
			displayStyle: 'text' as const,
		},
		getLineBreak('item-identifiers-lb2'),
		{
			id: 'category-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'category', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
			},
			displayStyle: 'text' as const,
		},
	],
	headers: [
		{ id: 'item-number-header', text: 'Item #' },
		{ id: 'ndc-header', text: 'NDC' },
		{ id: 'category-header', text: 'Category' },
	],
	sortable: false,
	alignment: 'left' as const,
	verticalAlignment: 'top' as const,
});

/**
 * Pricing cell: Target (price) / Minimum (minimumPrice).
 *
 * Axia's saved config also had a "Platform" row reading the tenant-specific
 * `cf:platformPrice` custom field; it is intentionally omitted here so the
 * default doesn't ship an empty column to tenants without that custom field.
 */
const getPricingColumn = (): ColumnConfiguration => ({
	id: 'pricing',
	fields: [
		{
			id: 'target-price-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'price', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'currency' as const,
		},
		getLineBreak('pricing-lb1'),
		{
			id: 'minimum-price-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'minimumPrice', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
			},
			displayStyle: 'currency' as const,
		},
	],
	headers: [
		{ id: 'target-price-header', text: 'Target' },
		{ id: 'minimum-price-header', text: 'Minimum' },
	],
	sortable: false,
	alignment: 'right' as const,
	verticalAlignment: 'top' as const,
});

/**
 * Stock-on-hand cell: On Hand / Soonest Exp / On Backorder.
 */
const getQuantityColumn = (): ColumnConfiguration => ({
	id: 'quantity',
	label: 'On Hand',
	width: 80,
	fields: [
		{
			id: 'quantity-value',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'quantityOnHand', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
				formatOptions: { numberFormat: 'auto' as const },
			},
			displayStyle: 'number' as const,
		},
		getLineBreak('quantity-lb1'),
		{
			id: 'soonest-expiration-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'soonestExpirationDate', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
				formatOptions: { dateFormat: 'short' as const },
			},
			displayStyle: 'date' as const,
		},
		getLineBreak('quantity-lb2'),
		{
			id: 'quantity-on-backorder-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'quantityOnBackorder', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
				formatOptions: { numberFormat: 'auto' as const },
			},
			displayStyle: 'number' as const,
		},
	],
	headers: [
		{ id: 'h5', text: 'On Hand' },
		{ id: 'soonest-expiration-header', text: 'Soonest Exp' },
		{ id: 'quantity-on-backorder-header', text: 'On Backorder' },
	],
	sortable: true,
	alignment: 'right' as const,
	sortFields: [
		{
			direction: 'DESC' as const,
			fieldName: 'quantityOnHand',
			objectTable: 'item',
		},
	],
	verticalAlignment: 'top' as const,
});

/**
 * A date value stacked below "Soonest Arr" in the On Order column. Used by the
 * Recently Restocked (restocked date) and Newly Stocked (added date) tabs.
 */
interface TrailingDateField {
	/** Stable field id within the column. */
	id: string;
	/** Header label shown for this stacked row. */
	header: string;
	/** The data source for the date value. */
	dataConfig: NonNullable<FieldConfiguration['dataConfig']>;
}

/**
 * On Order cell: On Order (quantityOnOrder) / Soonest Arr (soonestArrivalDate).
 *
 * When `trailingDate` is provided, a third row is stacked BELOW Soonest Arr —
 * this is where the Recently Restocked / Newly Stocked tabs surface their date.
 */
const getOnOrderColumn = (
	trailingDate?: TrailingDateField,
): ColumnConfiguration => ({
	id: 'on-order',
	fields: [
		{
			id: 'quantity-on-order-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'quantityOnOrder', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
				formatOptions: { numberFormat: 'auto' as const },
			},
			displayStyle: 'number' as const,
		},
		getLineBreak('on-order-lb1'),
		{
			id: 'soonest-arrival-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'soonestArrivalDate', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
				formatOptions: { dateFormat: 'short' as const },
			},
			displayStyle: 'date' as const,
		},
		...(trailingDate != null
			? [
					getLineBreak('on-order-lb2'),
					{
						id: trailingDate.id,
						dataConfig: trailingDate.dataConfig,
						styleConfig: {
							size: 'sm' as const,
							colorRule: {
								type: 'theme' as const,
								themeColor: 'textSecondary',
							},
							formatOptions: { dateFormat: 'short' as const },
						},
						displayStyle: 'date' as const,
					},
				]
			: []),
	],
	headers: [
		{ id: 'quantity-on-order-header', text: 'On Order' },
		{ id: 'soonest-arrival-header', text: 'Soonest Arr' },
		...(trailingDate != null
			? [{ id: `${trailingDate.id}-header`, text: trailingDate.header }]
			: []),
	],
	sortable: false,
	alignment: 'right' as const,
	verticalAlignment: 'top' as const,
});

/** Restocked date source for the Recently Restocked tab. */
const getRestockedDateConfig = (): TrailingDateField => ({
	id: 'restocked-date-field',
	header: 'Restocked',
	dataConfig: {
		type: 'field' as const,
		field: { fieldName: 'lastRestockedDate', objectTable: 'item' },
		defaultValue: '-',
	},
});

/**
 * Added date source for the Newly Stocked tab. Prefers externalCreatedDate (the
 * ERP-supplied date), falling back to the record's _created_date — matching the
 * COALESCE the filter/sort use so the displayed date lines up with the row set.
 */
const getAddedDateConfig = (): TrailingDateField => ({
	id: 'added-date-field',
	header: 'Added',
	dataConfig: {
		type: 'conditional' as const,
		conditions: [
			{
				field: 'externalCreatedDate',
				operator: 'not_equals' as const,
				value: null,
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'externalCreatedDate', objectTable: 'item' },
				},
			},
		],
		defaultDataConfig: {
			type: 'field' as const,
			field: { fieldName: '_created_date', objectTable: 'item' },
			defaultValue: '-',
		},
	},
});

/**
 * Item Cost cell: acquisitionCost.
 */
const getItemCostColumn = (): ColumnConfiguration => ({
	id: 'item-cost',
	fields: [
		{
			id: 'acquisition-cost-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'acquisitionCost', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'currency' as const,
		},
	],
	headers: [{ id: 'item-cost-header', text: 'Item Cost' }],
	sortable: false,
	alignment: 'right' as const,
	verticalAlignment: 'top' as const,
});

/**
 * Trailing-3-month sales cell: Sold Units (3M) / Revenue (3M).
 */
const getSalesAggregateColumn = (): ColumnConfiguration => ({
	id: 'sales-aggregate',
	fields: [
		{
			id: 'sold-units-aggregate-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'soldUnitsAggregate', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
				formatOptions: { numberFormat: 'auto' as const },
			},
			displayStyle: 'number' as const,
		},
		getLineBreak('sales-aggregate-lb1'),
		{
			id: 'revenue-aggregate-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'revenueAggregate', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
				formatOptions: {
					numberFormat: 'currency' as const,
					decimalPlaces: 0,
				},
			},
			displayStyle: 'currency' as const,
		},
	],
	headers: [
		{ id: 'sold-units-aggregate-header', text: 'Sold Units (3M)' },
		{ id: 'revenue-aggregate-header', text: 'Revenue (3M)' },
	],
	sortable: false,
	alignment: 'right' as const,
	verticalAlignment: 'top' as const,
});

/**
 * Trailing-3-month reach cell: Clients (3M) / Invoices (3M).
 */
const getClientReachColumn = (): ColumnConfiguration => ({
	id: 'client-reach',
	fields: [
		{
			id: 'unique-clients-aggregate-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'uniqueClientsAggregate', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
				formatOptions: { numberFormat: 'auto' as const },
			},
			displayStyle: 'number' as const,
		},
		getLineBreak('client-reach-lb1'),
		{
			id: 'unique-invoices-aggregate-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'uniqueInvoicesAggregate', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
				formatOptions: { numberFormat: 'auto' as const },
			},
			displayStyle: 'number' as const,
		},
	],
	headers: [
		{ id: 'unique-clients-aggregate-header', text: 'Clients (3M)' },
		{ id: 'unique-invoices-aggregate-header', text: 'Invoices (3M)' },
	],
	sortable: false,
	alignment: 'right' as const,
	verticalAlignment: 'top' as const,
});

/**
 * Rolling revenue cell: Revenue (30d) + trend / Revenue (90d) + trend. Trends
 * are colored red below zero and green above zero.
 */
const getRollingRevenueColumn = (): ColumnConfiguration => ({
	id: 'rolling-revenue',
	fields: [
		{
			id: 'rolling-revenue-30-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'rollingRevenue30', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
				formatOptions: {
					numberFormat: 'decimal' as const,
					decimalPlaces: 0,
				},
			},
			displayStyle: 'currency' as const,
		},
		{
			id: 'rolling-revenue-trend-30-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'rollingRevenueTrend30', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: {
					type: 'range-mapping' as const,
					rangeMapping: [
						{ max: -1, min: -100, color: { bg: '', text: 'red' } },
						{ max: 100, min: 1, color: { bg: '', text: 'green' } },
					],
				},
				formatOptions: {
					numberFormat: 'percent' as const,
					decimalPlaces: 0,
					percentValueIsWholeNumber: false,
				},
			},
			displayStyle: 'number' as const,
		},
		getLineBreak('rolling-revenue-lb1'),
		{
			id: 'rolling-revenue-90-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'rollingRevenue90', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
				formatOptions: {
					numberFormat: 'decimal' as const,
					decimalPlaces: 0,
				},
			},
			displayStyle: 'currency' as const,
		},
		{
			id: 'rolling-revenue-trend-90-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'rollingRevenueTrend90', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: {
					type: 'range-mapping' as const,
					rangeMapping: [
						{ max: -1, min: -100, color: { bg: '', text: 'red' } },
						{ max: 100, min: 1, color: { bg: '', text: 'green' } },
					],
				},
				formatOptions: {
					numberFormat: 'percent' as const,
					decimalPlaces: 0,
					percentValueIsWholeNumber: false,
				},
			},
			displayStyle: 'number' as const,
		},
	],
	headers: [
		{ id: 'rolling-revenue-30-header', text: 'Revenue (30d)' },
		{ id: 'rolling-revenue-90-header', text: 'Revenue (90d)' },
	],
	sortable: false,
	alignment: 'left' as const,
	verticalAlignment: 'top' as const,
});

/**
 * 1-year sales sparkline rendered by the item-client-history-trend-chart custom
 * component (bound to `id`, from which it loads the item's history).
 */
const getSalesTrendChartColumn = (): ColumnConfiguration => ({
	id: 'sales-trend-chart',
	fields: [
		{
			id: 'sales-trend-chart-field',
			dataConfig: {
				type: 'field' as const,
				field: { fieldName: 'id', objectTable: 'item' },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm' as const,
				colorRule: { type: 'default' as const },
			},
			displayStyle: 'custom' as const,
			customComponent: { id: 'item-client-history-trend-chart' },
		},
	],
	headers: [{ id: 'sales-trend-chart-header', text: '1Y Sales' }],
	sortable: false,
	alignment: 'left' as const,
	verticalAlignment: 'top' as const,
});

/**
 * The shared rich column layout used by every item tab. Recently Restocked and
 * Newly Stocked pass a `trailingDate` so their date appears below Soonest Arr.
 */
const getItemColumns = (
	trailingDate?: TrailingDateField,
): Array<ColumnConfiguration> => [
	getImageColumn(),
	getItemInfoColumn(),
	getItemIdentifiersColumn(),
	getPricingColumn(),
	getQuantityColumn(),
	getOnOrderColumn(trailingDate),
	getItemCostColumn(),
	getSalesAggregateColumn(),
	getClientReachColumn(),
	getRollingRevenueColumn(),
	getSalesTrendChartColumn(),
];

/**
 * Item list configuration
 * Main items list with multi-view support (All Items, Active Items, Recently
 * Restocked, Newly Stocked). Converted from JSON to TypeScript for type safety.
 */
export const ITEM_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-items-all',
			cacheTTL: 600000,
			name: 'All Items',
			type: 'default',
			active: true,
			columns: getItemColumns(),
			mobileCardConfig: ITEM_MOBILE_CARD_CONFIG,
			description: 'Complete inventory list with all items',
			defaultSortId: 'column-item-info-asc',
			sortOrder: 0,
		},
		{
			id: 'default-items-active',
			cacheTTL: 600000,
			name: 'Active Items',
			type: 'default',
			active: true,
			columns: getItemColumns(),
			mobileCardConfig: ITEM_MOBILE_CARD_CONFIG,
			description: 'Only active items',
			defaultSortId: 'column-item-info-asc',
			sortOrder: 1,
			filterRules: {
				itemStatus: {
					defaultValue: [{ id: 'Active', label: 'Active' }],
				},
			},
		},
		{
			id: 'default-items-recently-restocked',
			cacheTTL: 600000,
			name: 'Recently Restocked',
			type: 'default',
			active: true,
			columns: getItemColumns(getRestockedDateConfig()),
			mobileCardConfig: ITEM_MOBILE_CARD_CONFIG,
			description:
				'Items whose lastRestockedDate is within the last 30 days. Mirrors the client shop "Recently Restocked" tab.',
			defaultSortId: 'restocked-date-desc',
			sortOrder: 2,
			filterRules: {
				itemRestockedDateRange: {
					defaultValue: [
						{
							id: 'last-30-days',
							label: 'Last 30 Days',
							data: { startDate: '{{DATE:-30d}}', endDate: '{{DATE:0d}}' },
						},
					],
				},
			},
		},
		{
			id: 'default-items-newly-stocked',
			cacheTTL: 600000,
			name: 'Newly Stocked',
			type: 'default',
			active: true,
			columns: getItemColumns(getAddedDateConfig()),
			mobileCardConfig: ITEM_MOBILE_CARD_CONFIG,
			description:
				'Items created in the last 30 days, using COALESCE(externalCreatedDate, _created_date) so the result matches the client-shop "Newly Stocked" tab.',
			defaultSortId: 'added-date-desc',
			sortOrder: 3,
			filterRules: {
				itemCreatedDateRange: {
					defaultValue: [
						{
							id: 'last-30-days',
							label: 'Last 30 Days',
							data: { startDate: '{{DATE:-30d}}', endDate: '{{DATE:0d}}' },
						},
					],
				},
			},
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100, 200],
	},
	filters: {
		activeFilters: [
			'itemSearch',
			'itemCompany',
			'itemCategories',
			'itemManufacturers',
			'itemGroups',
			'itemStatus',
			'itemShortDate',
			'stock_status',
			'itemRestockedDateRange',
			'itemCreatedDateRange',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/items/:id',
			objectLabel: 'Item',
			objectTable: 'item',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: 'externalId2', fieldLabel: 'SKU' },
				{
					fieldName: 'extendedDescription',
					fieldLabel: 'Extended Description',
				},
				{ fieldName: 'manufacturer', fieldLabel: 'Manufacturer' },
				{ fieldName: 'manufacturerItemId', fieldLabel: 'Manufacturer Item ID' },
				{ fieldName: 'brandEquivalent', fieldLabel: 'Brand Equivalent' },
				{ fieldName: 'category', fieldLabel: 'Category' },
				{ fieldName: 'subcategory', fieldLabel: 'Subcategory' },
				{ fieldName: 'sizeCount', fieldLabel: 'Size Count' },
				{ fieldName: 'unitOfMeasure', fieldLabel: 'Unit of Measure' },
				{ fieldName: 'strength', fieldLabel: 'Strength' },
				{ fieldName: 'countryOfOrigin', fieldLabel: 'Country of Origin' },
				{ fieldName: 'family', fieldLabel: 'Family' },
				{ fieldName: 'storageType', fieldLabel: 'Storage Type' },
				{ fieldName: 'tradeStyle', fieldLabel: 'Trade Style' },
				{ fieldName: 'isActive', fieldLabel: 'Active' },
				{
					fieldName: 'ecommerceVisibility',
					fieldLabel: 'Ecommerce Visibility',
				},
				{ fieldName: 'price', fieldLabel: 'Price' },
				{ fieldName: 'minimumPrice', fieldLabel: 'Minimum Price' },
				{ fieldName: 'previousPrice', fieldLabel: 'Previous Price' },
				{
					fieldName: 'priceModifiedDatetime',
					fieldLabel: 'Price Modified Date',
				},
				{ fieldName: 'acquisitionCost', fieldLabel: 'Acquisition Cost' },
				{ fieldName: 'sellCost', fieldLabel: 'Sell Cost' },
				{ fieldName: 'averageCost', fieldLabel: 'Average Cost' },
				{
					fieldName: 'averageWholesalePrice',
					fieldLabel: 'Average Wholesale Price',
				},
				{ fieldName: 'quantityOnHand', fieldLabel: 'Quantity On Hand' },
				{ fieldName: 'quantityOnOrder', fieldLabel: 'Quantity On Order' },
				{
					fieldName: 'quantityOnBackorder',
					fieldLabel: 'Quantity On Backorder',
				},
				{ fieldName: 'lastImportDate', fieldLabel: 'Last Import Date' },
				{
					fieldName: 'externalCreatedDate',
					fieldLabel: 'External Created Date',
				},
				{ fieldName: 'lastRestockedDate', fieldLabel: 'Last Restocked Date' },
				{ fieldName: 'soonestArrivalDate', fieldLabel: 'Soonest Arrival Date' },
				{
					fieldName: 'soonestExpirationDate',
					fieldLabel: 'Soonest Expiration Date',
				},
				{
					fieldName: 'lastOutOfStockDate',
					fieldLabel: 'Last Out of Stock Date',
				},
				{ fieldName: 'upc', fieldLabel: 'UPC' },
				{ fieldName: 'caseUpc', fieldLabel: 'Case UPC' },
				{ fieldName: 'retailUpc', fieldLabel: 'Retail UPC' },
				{ fieldName: 'extraUpc', fieldLabel: 'Extra UPC' },
				{ fieldName: 'ndc', fieldLabel: 'NDC' },
				{ fieldName: 'standardNdc', fieldLabel: 'Standard NDC' },
				{ fieldName: 'isShortDate', fieldLabel: 'Short Date' },
				{ fieldName: 'isHazmat', fieldLabel: 'Hazmat' },
				{ fieldName: 'isRefrigerated', fieldLabel: 'Refrigerated' },
				{ fieldName: 'isHeavy', fieldLabel: 'Heavy' },
				{ fieldName: 'caseSize', fieldLabel: 'Case Size' },
				{ fieldName: 'unitsPerPallet', fieldLabel: 'Units Per Pallet' },
				{ fieldName: 'weight', fieldLabel: 'Weight' },
				{ fieldName: 'length', fieldLabel: 'Length' },
				{ fieldName: 'width', fieldLabel: 'Width' },
				{ fieldName: 'height', fieldLabel: 'Height' },
				{ fieldName: 'dailyLimit', fieldLabel: 'Daily Limit' },
				{ fieldName: 'monthlyLimit', fieldLabel: 'Monthly Limit' },
				{ fieldName: 'perOrderLimit', fieldLabel: 'Per Order Limit' },
				{ fieldName: 'conversionFactor', fieldLabel: 'Conversion Factor' },
				{ fieldName: 'invoiceFrequency', fieldLabel: 'Invoice Frequency' },
				{
					fieldName: 'soldUnitsAggregate',
					fieldLabel: '3-Month Sold Units',
				},
				{ fieldName: 'revenueAggregate', fieldLabel: '3-Month Revenue' },
				{
					fieldName: 'uniqueClientsAggregate',
					fieldLabel: '3-Month Unique Clients',
				},
				{
					fieldName: 'uniqueInvoicesAggregate',
					fieldLabel: '3-Month Unique Invoices',
				},
				{ fieldName: 'soldUnitsMtd', fieldLabel: 'Sold Units MTD' },
				{
					fieldName: 'soldUnitsLastMonth',
					fieldLabel: 'Sold Units Last Month',
				},
				{
					fieldName: 'soldUnitsTwoMonthsAgo',
					fieldLabel: 'Sold Units Two Months Ago',
				},
				{ fieldName: 'revenueMtd', fieldLabel: 'Revenue MTD' },
				{ fieldName: 'revenueLastMonth', fieldLabel: 'Revenue Last Month' },
				{
					fieldName: 'revenueTwoMonthsAgo',
					fieldLabel: 'Revenue Two Months Ago',
				},
				{ fieldName: 'uniqueClientsMtd', fieldLabel: 'Unique Clients MTD' },
				{
					fieldName: 'uniqueClientsLastMonth',
					fieldLabel: 'Unique Clients Last Month',
				},
				{
					fieldName: 'uniqueClientsTwoMonthsAgo',
					fieldLabel: 'Unique Clients Two Months Ago',
				},
				{ fieldName: 'uniqueInvoicesMtd', fieldLabel: 'Unique Invoices MTD' },
				{
					fieldName: 'uniqueInvoicesLastMonth',
					fieldLabel: 'Unique Invoices Last Month',
				},
				{
					fieldName: 'uniqueInvoicesTwoMonthsAgo',
					fieldLabel: 'Unique Invoices Two Months Ago',
				},
				{ fieldName: 'totalGpAggregate', fieldLabel: 'Total GP (Aggregate)' },
				{ fieldName: 'totalGpMargin', fieldLabel: 'Total GP Margin' },
				{
					fieldName: 'rollingTotalGpAggregate30',
					fieldLabel: 'Rolling GP (30 Days)',
				},
				{
					fieldName: 'rollingTotalGpAggregate90',
					fieldLabel: 'Rolling GP (90 Days)',
				},
				{
					fieldName: 'rollingTotalGpMargin30',
					fieldLabel: 'Rolling GP Margin (30 Days)',
				},
				{
					fieldName: 'rollingTotalGpMargin90',
					fieldLabel: 'Rolling GP Margin (90 Days)',
				},
				{
					fieldName: 'rollingRevenue30',
					fieldLabel: 'Rolling Revenue (30 Days)',
				},
				{
					fieldName: 'rollingRevenue90',
					fieldLabel: 'Rolling Revenue (90 Days)',
				},
				{
					fieldName: 'rollingTotalGpAggregateTrend30',
					fieldLabel: 'Rolling GP Trend (30 Days)',
				},
				{
					fieldName: 'rollingTotalGpAggregateTrend90',
					fieldLabel: 'Rolling GP Trend (90 Days)',
				},
				{
					fieldName: 'rollingTotalGpMarginTrend30',
					fieldLabel: 'Rolling GP Margin Trend (30 Days)',
				},
				{
					fieldName: 'rollingTotalGpMarginTrend90',
					fieldLabel: 'Rolling GP Margin Trend (90 Days)',
				},
				{
					fieldName: 'rollingRevenueTrend30',
					fieldLabel: 'Rolling Revenue Trend (30 Days)',
				},
				{
					fieldName: 'rollingRevenueTrend90',
					fieldLabel: 'Rolling Revenue Trend (90 Days)',
				},
				{ fieldName: 'createdDate', fieldLabel: 'Created Date' },
				{ fieldName: 'modifiedDate', fieldLabel: 'Modified Date' },
				{ fieldName: 'ownerUserId', fieldLabel: 'Owner User ID' },
				{ fieldName: 'creatorUserId', fieldLabel: 'Creator User ID' },
			],
		},
	],
	tableSorts: [
		{
			id: 'relevance',
			name: 'Relevance',
			fields: [
				{ direction: 'DESC', fieldName: 'relevance', objectTable: 'item' },
			],
			description: 'Sort by search relevance',
		},
		{
			id: 'name',
			name: 'Name',
			fields: [
				{ direction: 'ASC', fieldName: 'description', objectTable: 'item' },
			],
			description: 'Sort items by name',
		},
		{
			id: 'price',
			name: 'Price',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'price',
					objectTable: 'item',
					nullsLast: true,
				},
			],
			description: 'Sort items by price',
		},
		{
			id: 'category',
			name: 'Category',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'category',
					objectTable: 'item',
					nullsLast: true,
				},
				{
					direction: 'ASC',
					fieldName: 'subcategory',
					objectTable: 'item',
					nullsLast: true,
				},
			],
			description: 'Sort by category then subcategory',
		},
		{
			id: 'manufacturer',
			name: 'Manufacturer',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'manufacturer',
					objectTable: 'item',
					nullsLast: true,
				},
			],
			description: 'Sort by manufacturer name',
		},
		{
			id: 'stock',
			name: 'Stock Level',
			fields: [
				{ direction: 'DESC', fieldName: 'quantityOnHand', objectTable: 'item' },
			],
			description: 'Sort by quantity on hand',
		},
		{
			// Name carries the "(Newest First)" direction indicator so
			// mergeSortOptions keeps this id as-is (instead of generating
			// `-asc`/`-desc` variants). That lets the `-desc` id match both the
			// data-sort init (exact id match) and the sort-dropdown's active label.
			id: 'restocked-date-desc',
			name: 'Restocked Date (Newest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lastRestockedDate',
					objectTable: 'item',
					nullsLast: true,
				},
			],
			description: 'Sort by most recently restocked',
		},
		{
			// See note on restocked-date-desc above re: the direction indicator.
			id: 'added-date-desc',
			name: 'Added Date (Newest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'externalCreatedDate',
					objectTable: 'item',
					nullsLast: true,
				},
				{
					direction: 'DESC',
					fieldName: '_created_date',
					objectTable: 'item',
				},
			],
			description: 'Sort by most recently added',
		},
	],
	importExport: {
		allowExport: true,
		allowImport: true,
		exportFields: [
			'description',
			'externalId',
			'manufacturer',
			'price',
			'category',
			'subcategory',
			'unitOfMeasure',
			'isActive',
			'quantityOnHand',
			'primaryImageUrl',
		],
		importFields: [
			'description',
			'externalId',
			'manufacturer',
			'price',
			'category',
			'subcategory',
			'unitOfMeasure',
			'isActive',
		],
	},
};
